import { BN } from '@bifrost-platform/bifront-sdk-react-biholder';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ImageHomeMainDarkmode from '@/assets/images/image-home-main-darkmode.webp';
import ImageHomeMainMobileDarkmode from '@/assets/images/image-home-main-mobile-darkmode.png';
import ImageHomeMainMobile from '@/assets/images/image-home-main-mobile.png';
import ImageHomeMain from '@/assets/images/image-home-main.webp';
import { ColumnCenter } from '@/components/Flex';
import {
  GAP_LAYOUT_MOBILE,
  GAP_LAYOUT_TABLET,
  PADDING_LAYOUT_VERTICAL,
  PADDING_LAYOUT_VERTICAL_MOBILE,
  PADDING_LAYOUT_VERTICAL_TABLET,
} from '@/components/Layout';
import Spline from '@/components/draw/Spline';
import { HEIGHT_HEADER } from '@/components/header';
import LandingTitle from '@/components/text/LandingTitle';
import StyledText from '@/components/text/StyledText';
import Underline from '@/components/text/Underline';
import ANNUAL_RETURN_RATE from '@/configs/annualReturnRate';
import { MEDIA_QUERY_MOBILE, MEDIA_QUERY_TABLET } from '@/configs/mediaQuery';
import {
  SPLINE_SCENE_URL_HOME_MAIN,
  SPLINE_SCENE_URL_HOME_MAIN_DARK,
} from '@/configs/splineSceneUrl';
import useBifi from '@/hooks/api/bifi/useBifi';
import useEverdex from '@/hooks/api/everdex/useEverdex';
import useBtcfiMarket from '@/hooks/api/useBtcfiMarket';
import useDeviceSize from '@/hooks/mobile/useDeviceSize';
import useIsMobile from '@/hooks/mobile/useIsMobile';
import useGetCurrencyString from '@/hooks/store/useGetCurrencyString';
import useDarkMode from '@/hooks/theme/useDarkMode';
import useVerticalScrollDetector from '@/hooks/useVerticalScrollDetector';
import { formatRate } from '@/libs/formatNumber';
import CountingData from './CountingData';
import SectionHomePage from './SectionHomePage';

const Root = styled.div`
  position: relative;

  @media (${MEDIA_QUERY_TABLET}) {
    margin-top: ${-PADDING_LAYOUT_VERTICAL_TABLET}px;
  }
  @media (${MEDIA_QUERY_MOBILE}) {
    margin-top: ${-PADDING_LAYOUT_VERTICAL_MOBILE}px;
  }
`;
const VideoContainer = styled.div`
  position: absolute;
  top: ${120 - HEIGHT_HEADER - PADDING_LAYOUT_VERTICAL}px;
  width: 1920px;
  height: 1080px;
`;
const ImageContainer = styled.div`
  position: absolute;
  bottom: ${-(GAP_LAYOUT_MOBILE / 2 + 20)}px;
  width: 680px;
  height: 544px;
  z-index: -1;

  & > img {
    width: 680px;
    height: 544px;
  }

  @media (${MEDIA_QUERY_MOBILE}) {
    bottom: ${-(GAP_LAYOUT_MOBILE * 1.3)}px;
    width: 600px;
    height: 480px;

    & > img {
      width: 600px;
      height: 480px;
    }
  }
`;
const Container = styled(ColumnCenter)`
  gap: 48px;
  z-index: 0;
  padding-top: 160px;
  padding-bottom: 123px;

  @media (${MEDIA_QUERY_MOBILE}) {
    padding-top: 0px;
  }
`;

export default function MainSection() {
  // device size
  const deviceSize = useDeviceSize();

  // translate
  const { t } = useTranslation('home', { keyPrefix: 'main' });

  // darkmode
  const { isDarkMode } = useDarkMode();

  // is mobile
  const isMobile = useIsMobile();

  // currency
  const { currencySymbol } = useGetCurrencyString();

  // scroll detector
  const { isScrolled } = useVerticalScrollDetector();

  // btcfi
  const { tvlValue } = useBtcfiMarket();

  // bifi
  // const { apr: bifiApr } = useBifi();
  const {} = useBifi();

  // everdex
  // const { apr: everdexApr } = useEverdex();
  const {} = useEverdex();

  // state
  const [isMounted, setIsMounted] = useState(false);

  // memo
  const splineUrl = useMemo(
    () =>
      isDarkMode ? SPLINE_SCENE_URL_HOME_MAIN_DARK : SPLINE_SCENE_URL_HOME_MAIN,
    [isDarkMode]
  );
  const splineVariables = useMemo(
    () => ({ isDarkMode: isDarkMode ? 'dark' : 'light' }),
    [isDarkMode]
  );
  const annualReturnRate = useMemo(
    () => formatRate(ANNUAL_RETURN_RATE),
    []
    // () =>
    //   formatRate(
    //     BN.max(
    //       isNormalPositive(bifiApr)
    //         ? BN.max(bifiApr, everdexApr).minus(BORROW_APY)
    //         : 0,
    //       0
    //     )
    //   ),
    // [bifiApr, everdexApr]
  );

  // effect
  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <Root>
      <SectionHomePage
        justify="flex-start"
        height={deviceSize === 'mobile' ? 732 : undefined}
        margin={{
          bottom:
            deviceSize !== 'desktop' ? -(GAP_LAYOUT_TABLET / 2) : undefined,
        }}
      >
        {isMounted &&
          (isMobile ? (
            <>
              {isDarkMode && (
                <ImageContainer>
                  <Image unoptimized alt="" src={ImageHomeMainMobileDarkmode} />
                </ImageContainer>
              )}
              {!isDarkMode && (
                <ImageContainer>
                  <Image unoptimized alt="" src={ImageHomeMainMobile} />
                </ImageContainer>
              )}
            </>
          ) : (
            <VideoContainer>
              <Spline
                url={splineUrl}
                variables={splineVariables}
                loadingNode={
                  isDarkMode ? (
                    <Image
                      alt=""
                      width={1920}
                      height={1080}
                      src={ImageHomeMainDarkmode}
                    />
                  ) : (
                    <Image
                      alt=""
                      width={1920}
                      height={1080}
                      src={ImageHomeMain}
                    />
                  )
                }
              />
            </VideoContainer>
          ))}

        <Container>
          <ColumnCenter $gap="xxs">
            <StyledText $fontSize="md" $fontWeight="bold" $color="active">
              {t('tvl')}
            </StyledText>
            <Underline $fontSize="xxxl" $fontWeight="bold">
              <CountingData
                value={new BN(tvlValue).toFixed(0, BN.ROUND_HALF_UP)}
                decimal={0}
                prefix={currencySymbol}
                isScrolled={isScrolled}
              />
            </Underline>
          </ColumnCenter>
          <ColumnCenter $gap="xl">
            <LandingTitle>{t('title')}</LandingTitle>
            <StyledText
              $fontSize="md"
              $fontWeight="regular"
              $color="secondary"
              $textAlign="center"
              $whiteSpace="pre-wrap"
            >
              {t('description', { 0: annualReturnRate })}
            </StyledText>
          </ColumnCenter>
        </Container>
      </SectionHomePage>
    </Root>
  );
}
